import type Konva from 'konva'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Circle, Ellipse, Group, Line, Path, Rect, Text } from 'react-konva'
import { GEAR_DEFINITIONS, getGearSnapPoints, SPAN_GEAR_TYPES } from './gear'
import { GearIcon } from './gear-icon'
import { BasicShapeElement, Calibration, DrawingElement, GearElement, GROUND_Y, Point, RopeElement, RopeHandleDrag, RopeHandleRef, SnapCandidate } from './types'
import { buildContactTypes, getRopeControlPoints, getRopeControlPointsWithDrag, getRopePath, resolveRopePoint } from './rope-geometry'
import { formatAngle, formatLength, interiorAngleDeg, pointAtFraction, segmentsLength } from './measurements'
import { getRopeRoleStyle } from './rope-roles'
import { deviationSeverity, formatLoadFactor, redirectLoadFactor, SEVERITY_COLOR } from './warnings'

export type SelectHandler = (id: string, additive: boolean) => void
export type RegisterNodeRef = (id: string, node: Konva.Node | null) => void

export function isAdditive(event: Konva.KonvaEventObject<MouseEvent | TouchEvent | Event>): boolean {
  const native = event.evt as MouseEvent & TouchEvent
  return Boolean(native?.shiftKey || native?.ctrlKey || native?.metaKey)
}

type GearNodeProps = {
  element: GearElement
  selected: boolean
  onSelect: SelectHandler
  onDragStart: (id: string) => void
  onDragMove: (id: string, x: number, y: number) => void
  onDragEnd: (id: string, x: number, y: number) => void
  registerNodeRef?: RegisterNodeRef
  draggable?: boolean
}

export function GearNode({ element, selected, onSelect, onDragStart, onDragMove, onDragEnd, registerNodeRef, draggable = true }: GearNodeProps) {
  const snapPoints = getGearSnapPoints(element)
  const def = GEAR_DEFINITIONS[element.gearType]

  return (
    <Group
      ref={(node) => {
        registerNodeRef?.(element.id, node)
        if (node) {
          // Override getClientRect so the Transformer computes its bounding
          // box from the gear's own width/height instead of the union of all
          // children.  The label Text extends well beyond the gear bounds
          // (width + 120) which otherwise pushes the Transformer anchors far
          // from the actual gear edges, making resize handles unreachable.
          node.getClientRect = (config?: { skipTransform?: boolean; relativeTo?: Konva.Node }) => {
            const w = node.width()
            const h = node.height()
            if (config?.skipTransform) {
              return { x: 0, y: 0, width: w, height: h }
            }
            const transform = node.getAbsoluteTransform(config?.relativeTo)
            const points = [
              transform.point({ x: 0, y: 0 }),
              transform.point({ x: w, y: 0 }),
              transform.point({ x: w, y: h }),
              transform.point({ x: 0, y: h }),
            ]
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
            for (const p of points) {
              minX = Math.min(minX, p.x)
              minY = Math.min(minY, p.y)
              maxX = Math.max(maxX, p.x)
              maxY = Math.max(maxY, p.y)
            }
            return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
          }
        }
      }}
      id={element.id}
      name="gear-node"
      x={element.x + element.width / 2}
      y={element.y + element.height / 2}
      offsetX={element.width / 2}
      offsetY={element.height / 2}
      width={element.width}
      height={element.height}
      rotation={element.rotation}
      draggable={draggable}
      onClick={(event) => onSelect(element.id, isAdditive(event))}
      onTap={(event) => onSelect(element.id, isAdditive(event))}
      onDragStart={draggable ? () => onDragStart(element.id) : undefined}
      onDragMove={draggable ? (event) => onDragMove(element.id, event.target.x() - element.width / 2, event.target.y() - element.height / 2) : undefined}
      onDragEnd={draggable ? (event) => onDragEnd(element.id, event.target.x() - element.width / 2, event.target.y() - element.height / 2) : undefined}
    >
      <Rect
        x={-6}
        y={-6}
        width={element.width + 12}
        height={element.height + 12}
        fill={selected ? 'rgba(37,99,235,0.08)' : 'transparent'}
        stroke={selected ? '#2563eb' : 'transparent'}
        strokeWidth={1.5}
        cornerRadius={8}
      />
      <Group width={element.width} height={element.height}>
        <GearIcon element={element} />
      </Group>
      <Text
        text={element.label?.trim() ? element.label : def.label}
        x={-60}
        y={element.height + 7}
        width={element.width + 120}
        align="center"
        fill="#334155"
        fontSize={11}
        fontStyle="600"
      />
      {typeof element.legendNumber === 'number' && (
        <Group x={element.width - 4} y={-4}>
          <Circle
            radius={9}
            fill="#1e40af"
            stroke="#fff"
            strokeWidth={1.5}
          />
          <Text
            text={String(element.legendNumber)}
            x={-9}
            y={-6}
            width={18}
            height={12}
            align="center"
            verticalAlign="middle"
            fill="#fff"
            fontSize={element.legendNumber >= 100 ? 7 : element.legendNumber >= 10 ? 8 : 9}
            fontStyle="700"
          />
        </Group>
      )}
      {selected && snapPoints.map((snap) => {
        const isSpan = SPAN_GEAR_TYPES.has(element.gearType)
        let dotX: number
        let dotY: number
        if (isSpan) {
          const inset = element.height * (snap.x / 64)
          dotX = snap.x <= 32
            ? inset
            : element.width - element.height * ((64 - snap.x) / 64)
          dotY = snap.y * (element.height / 64)
        } else {
          dotX = snap.x * (element.width / 64)
          dotY = snap.y * (element.height / 64)
        }
        return (
          <Circle
            key={snap.id}
            x={dotX}
            y={dotY}
            radius={4}
            fill="#f8fafc"
            stroke={snap.role === 'pulley-sheave' || snap.role === 'deviation-contact' ? '#16a34a' : '#2563eb'}
            strokeWidth={2}
          />
        )
      })}
    </Group>
  )
}

type RopeNodeProps = {
  rope: RopeElement
  gears: GearElement[]
  selected: boolean
  onSelect: SelectHandler
  handleDrag?: RopeHandleDrag | null
  onHandleDragStart?: (ref: RopeHandleRef) => void
  onHandleDragMove?: (ref: RopeHandleRef, x: number, y: number) => Point | void
  onHandleDragEnd?: (ref: RopeHandleRef, x: number, y: number) => void
}

export function RopeNode({ rope, gears, selected, onSelect, handleDrag, onHandleDragStart, onHandleDragMove, onHandleDragEnd }: RopeNodeProps) {
  const basePoints = getRopeControlPointsWithDrag(rope, gears, handleDrag ?? null)
  const midpointDragAfter = (handleDrag && handleDrag.ropeId === rope.id && handleDrag.ref.kind === 'midpoint')
    ? handleDrag.ref.afterIndex
    : undefined

  // A free end shows the drawn path by default (a straight taut leg, no ground
  // dangle). `fallsToGround` is the opt-in for a line that truly hangs to the
  // deck — only then do we add the drop-to-ground tail.
  const startIsFree = rope.start.mode === 'free'
  const endIsFree = rope.end.mode === 'free'
  const startIsFreeGround = rope.start.mode === 'free' && rope.start.fallsToGround
  const endIsFreeGround = rope.end.mode === 'free' && rope.end.fallsToGround

  const points: Point[] = []
  if (startIsFreeGround) {
    const tailOrigin = rope.contacts.length > 0
      ? basePoints[1]
      : basePoints[0]
    points.push({ x: tailOrigin.x, y: Math.max(tailOrigin.y + 40, GROUND_Y) })
  }
  points.push(...basePoints)
  if (startIsFreeGround && rope.contacts.length > 0) {
    const firstContactPt = basePoints[1]
    points[1] = { x: firstContactPt.x, y: firstContactPt.y }
  }
  if (endIsFreeGround) {
    if (rope.contacts.length > 0) {
      const lastContactPt = basePoints[basePoints.length - 2]
      points[points.length - 1] = { x: lastContactPt.x, y: lastContactPt.y }
      points.push({ x: lastContactPt.x, y: Math.max(lastContactPt.y + 40, GROUND_Y) })
    } else {
      const ep = basePoints[basePoints.length - 1]
      points.push({ x: ep.x, y: Math.max(ep.y + 40, GROUND_Y) })
    }
  }

  const contactTypeArr = buildContactTypes(rope, midpointDragAfter, {
    startTail: startIsFreeGround,
    endTail: endIsFreeGround,
    startPath: startIsFree && !startIsFreeGround,
    endPath: endIsFree && !endIsFreeGround,
  })
  const path = getRopePath(points, GROUND_Y, contactTypeArr, rope.sagMultiplier, rope.autoSag)
  const startResolved = resolveRopePoint(rope.start, gears)
  const endResolved = resolveRopePoint(rope.end, gears)
  const draggingThisRope = Boolean(handleDrag && handleDrag.ropeId === rope.id)
  const draggingMidpoint = draggingThisRope && handleDrag?.ref.kind === 'midpoint'
  const editable = Boolean(onHandleDragStart && onHandleDragMove && onHandleDragEnd)

  const roleStyle = getRopeRoleStyle(rope.role)

  const startHandle = basePoints[0] ?? startResolved
  const endHandle = basePoints[basePoints.length - 1] ?? endResolved
  const contactHandles = rope.contacts.map((_, contactIndex) => {
    if (draggingThisRope && handleDrag?.ref.kind === 'contact' && handleDrag.ref.index === contactIndex) {
      return handleDrag.position
    }
    const idx = draggingMidpoint && handleDrag?.ref.kind === 'midpoint' && contactIndex >= handleDrag.ref.afterIndex
      ? contactIndex + 2
      : contactIndex + 1
    return basePoints[idx] ?? { x: 0, y: 0 }
  })

  return (
    <Group onClick={(event) => onSelect(rope.id, isAdditive(event))} onTap={(event) => onSelect(rope.id, isAdditive(event))}>
      <Path
        data={path}
        stroke={selected ? '#2563eb' : rope.color}
        strokeWidth={selected ? rope.diameter + 1.5 : rope.diameter}
        dash={roleStyle?.dash ?? undefined}
        lineCap="round"
        lineJoin="round"
        shadowColor="rgba(15,23,42,0.22)"
        shadowBlur={selected ? 4 : 2}
        listening
      />
      {selected && editable && (
        <>
          <RopeHandle
            ropeId={rope.id}
            handleRef={{ ropeId: rope.id, kind: 'endpoint', end: 'start' }}
            point={startHandle}
            color="#2563eb"
            onDragStart={onHandleDragStart}
            onDragMove={onHandleDragMove}
            onDragEnd={onHandleDragEnd}
          />
          <RopeHandle
            ropeId={rope.id}
            handleRef={{ ropeId: rope.id, kind: 'endpoint', end: 'end' }}
            point={endHandle}
            color="#2563eb"
            onDragStart={onHandleDragStart}
            onDragMove={onHandleDragMove}
            onDragEnd={onHandleDragEnd}
          />
          {contactHandles.map((point, index) => (
            <RopeHandle
              key={`${rope.id}-contact-${index}`}
              ropeId={rope.id}
              handleRef={{ ropeId: rope.id, kind: 'contact', index }}
              point={point}
              color="#16a34a"
              onDragStart={onHandleDragStart}
              onDragMove={onHandleDragMove}
              onDragEnd={onHandleDragEnd}
            />
          ))}
          {(!draggingThisRope || draggingMidpoint) && basePoints.slice(0, -1).map((point, segIndex) => {
            const next = basePoints[segIndex + 1]
            const mid = { x: (point.x + next.x) / 2, y: (point.y + next.y) / 2 }
            return (
              <RopeMidpointHandle
                key={`${rope.id}-mid-${segIndex}`}
                ropeId={rope.id}
                handleRef={{ ropeId: rope.id, kind: 'midpoint', afterIndex: segIndex }}
                point={mid}
                onDragStart={onHandleDragStart}
                onDragMove={onHandleDragMove}
                onDragEnd={onHandleDragEnd}
              />
            )
          })}
        </>
      )}
      {selected && !editable && basePoints.map((point, index) => (
        <Circle
          key={`${rope.id}-point-${index}`}
          x={point.x}
          y={point.y}
          radius={5}
          fill="#f8fafc"
          stroke="#2563eb"
          strokeWidth={2}
        />
      ))}
    </Group>
  )
}

type RopeHandleProps = {
  ropeId: string
  handleRef: RopeHandleRef
  point: { x: number; y: number }
  color?: string
  onDragStart?: (ref: RopeHandleRef) => void
  onDragMove?: (ref: RopeHandleRef, x: number, y: number) => Point | void
  onDragEnd?: (ref: RopeHandleRef, x: number, y: number) => void
}

function RopeHandle({ handleRef, point, color = '#2563eb', onDragStart, onDragMove, onDragEnd }: RopeHandleProps) {
  return (
    <Circle
      x={point.x}
      y={point.y}
      radius={7}
      fill="#f8fafc"
      stroke={color}
      strokeWidth={2.5}
      draggable
      onMouseDown={(event) => { event.cancelBubble = true }}
      onTouchStart={(event) => { event.cancelBubble = true }}
      onClick={(event) => { event.cancelBubble = true }}
      onTap={(event) => { event.cancelBubble = true }}
      onDragStart={(event) => {
        event.cancelBubble = true
        onDragStart?.(handleRef)
      }}
      onDragMove={(event) => {
        event.cancelBubble = true
        const effective = onDragMove?.(handleRef, event.target.x(), event.target.y())
        if (effective) event.target.position(effective)
      }}
      onDragEnd={(event) => {
        event.cancelBubble = true
        onDragEnd?.(handleRef, event.target.x(), event.target.y())
      }}
    />
  )
}

function RopeMidpointHandle({ handleRef, point, onDragStart, onDragMove, onDragEnd }: RopeHandleProps) {
  const [hovered, setHovered] = useState(false)
  const stageRef = useRef<Konva.Stage | null>(null)

  useEffect(() => {
    return () => {
      if (stageRef.current) stageRef.current.container().style.cursor = ''
    }
  }, [])

  return (
    <Group
      x={point.x}
      y={point.y}
      draggable
      onMouseEnter={(event) => {
        setHovered(true)
        const stage = event.target.getStage()
        stageRef.current = stage
        if (stage) stage.container().style.cursor = 'grab'
      }}
      onMouseLeave={(event) => {
        setHovered(false)
        const stage = event.target.getStage()
        if (stage) stage.container().style.cursor = ''
      }}
      onMouseDown={(event) => { event.cancelBubble = true }}
      onTouchStart={(event) => { event.cancelBubble = true }}
      onClick={(event) => { event.cancelBubble = true }}
      onTap={(event) => { event.cancelBubble = true }}
      onDragStart={(event) => {
        event.cancelBubble = true
        const stage = event.target.getStage()
        stageRef.current = stage
        if (stage) stage.container().style.cursor = 'grabbing'
        onDragStart?.(handleRef)
      }}
      onDragMove={(event) => {
        event.cancelBubble = true
        const effective = onDragMove?.(handleRef, event.target.x(), event.target.y())
        if (effective) event.target.position(effective)
      }}
      onDragEnd={(event) => {
        event.cancelBubble = true
        const stage = event.target.getStage()
        if (stage) stage.container().style.cursor = ''
        onDragEnd?.(handleRef, event.target.x(), event.target.y())
        event.target.position({ x: point.x, y: point.y })
      }}
    >
      <Circle
        radius={8}
        fill={hovered ? '#e0f2fe' : '#f1f5f9'}
        stroke={hovered ? '#0284c7' : '#64748b'}
        strokeWidth={1.5}
        opacity={0.95}
      />
      <Text
        text="+"
        fontSize={14}
        fontStyle="bold"
        fill={hovered ? '#0284c7' : '#475569'}
        align="center"
        verticalAlign="middle"
        width={16}
        height={16}
        offsetX={8}
        offsetY={8}
        listening={false}
      />
      {hovered && (
        <Text
          text="Drag onto gear to add contact"
          fontSize={11}
          fill="#334155"
          padding={4}
          offsetX={-12}
          offsetY={-4}
          listening={false}
        />
      )}
    </Group>
  )
}

type BasicShapeNodeProps = {
  element: BasicShapeElement
  selected: boolean
  onSelect: SelectHandler
  onDragStart?: (id: string) => void
  onDragMove?: (id: string, x: number, y: number) => void
  onDragEnd?: (id: string, x: number, y: number) => void
  onDoubleClickText?: (id: string) => void
  registerNodeRef?: RegisterNodeRef
  draggable?: boolean
}

export function BasicShapeNode({ element, selected, onSelect, onDragStart, onDragMove, onDragEnd, onDoubleClickText, registerNodeRef, draggable: draggableProp = true }: BasicShapeNodeProps) {
  const { id, shapeType, x, y, width, height, rotation, text, stroke, fill, fontSize } = element
  const cx = width / 2
  const cy = height / 2
  const draggable = draggableProp && Boolean(onDragStart && onDragMove && onDragEnd)
  const selectedStroke = selected ? '#2563eb' : stroke

  const handleDoubleClick = shapeType === 'text' && onDoubleClickText
    ? (event: Konva.KonvaEventObject<MouseEvent>) => {
        event.cancelBubble = true
        onDoubleClickText(id)
      }
    : undefined
  const handleDoubleTap = shapeType === 'text' && onDoubleClickText
    ? (event: Konva.KonvaEventObject<TouchEvent>) => {
        event.cancelBubble = true
        onDoubleClickText(id)
      }
    : undefined

  return (
    <Group
      ref={(node) => registerNodeRef?.(id, node)}
      id={id}
      name={`shape-node shape-${shapeType}`}
      x={x + cx}
      y={y + cy}
      offsetX={cx}
      offsetY={cy}
      width={Math.abs(width) || 1}
      height={Math.abs(height) || 1}
      rotation={rotation}
      draggable={draggable}
      onClick={(event) => onSelect(id, isAdditive(event))}
      onTap={(event) => onSelect(id, isAdditive(event))}
      onDblClick={handleDoubleClick}
      onDblTap={handleDoubleTap}
      onDragStart={draggable ? () => onDragStart!(id) : undefined}
      onDragMove={draggable ? (event) => onDragMove!(id, event.target.x() - cx, event.target.y() - cy) : undefined}
      onDragEnd={draggable ? (event) => onDragEnd!(id, event.target.x() - cx, event.target.y() - cy) : undefined}
    >
      {(shapeType === 'line' || shapeType === 'arrow') && (
        <>
          <Line points={[0, 0, width, height]} stroke={selectedStroke} strokeWidth={3} lineCap="round" />
          {shapeType === 'arrow' && (() => {
            const angle = Math.atan2(height, width)
            const arrowSize = 12
            const arrowA = angle + Math.PI - 0.45
            const arrowB = angle + Math.PI + 0.45
            return (
              <Line
                points={[
                  width,
                  height,
                  width + Math.cos(arrowA) * arrowSize,
                  height + Math.sin(arrowA) * arrowSize,
                  width,
                  height,
                  width + Math.cos(arrowB) * arrowSize,
                  height + Math.sin(arrowB) * arrowSize,
                ]}
                stroke={selectedStroke}
                strokeWidth={3}
                lineCap="round"
              />
            )
          })()}
        </>
      )}
      {shapeType === 'text' && (
        <Text
          x={0}
          y={0}
          width={Math.max(Math.abs(width), 40)}
          text={text || 'Text'}
          fill={selectedStroke}
          fontSize={fontSize ?? 20}
        />
      )}
      {shapeType === 'ellipse' && (
        <Ellipse
          x={Math.abs(width) / 2}
          y={Math.abs(height) / 2}
          radiusX={Math.abs(width) / 2}
          radiusY={Math.abs(height) / 2}
          stroke={selectedStroke}
          fill={fill}
          strokeWidth={2}
        />
      )}
      {shapeType === 'rect' && (
        <Rect
          x={0}
          y={0}
          width={Math.abs(width)}
          height={Math.abs(height)}
          stroke={selectedStroke}
          fill={fill}
          strokeWidth={2}
          cornerRadius={4}
        />
      )}
      {shapeType === 'i-beam' && (() => {
        const len = Math.hypot(width, height)
        if (len < 1) return null
        const angle = Math.atan2(height, width)
        const sin = Math.sin(angle)
        const cos = Math.cos(angle)
        const px = -sin // perpendicular unit x
        const py = cos  // perpendicular unit y

        // Cross-section dimensions matching icons.ts I-beam proportions
        const depth = Math.max(18, len * 0.12)
        const flangeH = depth * 0.18
        const webH = depth * 0.64

        // Build a filled rectangle running from (0,0) to (width,height),
        // offset perpendicular by `center` with half-thickness `half`
        const band = (center: number, half: number): number[] => [
          px * (center - half), py * (center - half),
          width + px * (center - half), height + py * (center - half),
          width + px * (center + half), height + py * (center + half),
          px * (center + half), py * (center + half),
        ]

        const flangeCenter = (depth - flangeH) / 2
        const flangeColor = selected ? '#60a5fa' : '#6B7280'
        const webColor = selected ? '#dbeafe' : '#D1D5DB'
        const borderColor = selected ? '#2563eb' : '#4B5563'

        return (
          <>
            {/* Web (light gray center) */}
            <Line points={band(0, webH / 2)} closed fill={webColor} stroke={borderColor} strokeWidth={1} />
            {/* Top flange */}
            <Line points={band(-flangeCenter, flangeH / 2)} closed fill={flangeColor} stroke={borderColor} strokeWidth={1} />
            {/* Bottom flange */}
            <Line points={band(flangeCenter, flangeH / 2)} closed fill={flangeColor} stroke={borderColor} strokeWidth={1} />
          </>
        )
      })()}
    </Group>
  )
}

type SnapHighlightProps = {
  candidate: SnapCandidate | null
}

export function SnapHighlight({ candidate }: SnapHighlightProps) {
  if (!candidate) return null
  return (
    <Group>
      <Circle x={candidate.point.x} y={candidate.point.y} radius={12} fill="rgba(34,197,94,0.18)" stroke="#16a34a" strokeWidth={2} />
      <Circle x={candidate.point.x} y={candidate.point.y} radius={4} fill="#16a34a" />
    </Group>
  )
}

type MeasurementLabelProps = {
  x: number
  y: number
  text: string
  color: string
  fontSize: number
  strokeWidth: number
}

function MeasurementLabel({ x, y, text, color, fontSize, strokeWidth }: MeasurementLabelProps) {
  const width = text.length * fontSize * 0.62 + fontSize * 0.8
  const height = fontSize * 1.5
  return (
    <Group x={x} y={y} listening={false}>
      <Rect
        x={-width / 2}
        y={-height / 2}
        width={width}
        height={height}
        fill="#ffffff"
        stroke={color}
        strokeWidth={strokeWidth}
        cornerRadius={height * 0.25}
        opacity={0.96}
      />
      <Text
        x={-width / 2}
        y={-height / 2}
        width={width}
        height={height}
        text={text}
        fill={color}
        fontSize={fontSize}
        fontStyle="bold"
        align="center"
        verticalAlign="middle"
      />
    </Group>
  )
}

const LENGTH_COLOR = '#0f766e'
const CLEARANCE_COLOR = '#7c3aed'

type MeasurementLayerProps = {
  elements: DrawingElement[]
  gears: GearElement[]
  calibration: Calibration | null
  zoom: number
  groundY?: number
}

/** Read-only overlay drawing length, deviation-angle and fall-clearance labels. */
export function MeasurementLayer({ elements, gears, calibration, zoom, groundY = GROUND_Y }: MeasurementLayerProps) {
  const fontSize = 12 / Math.max(zoom, 0.05)
  const strokeWidth = 1 / Math.max(zoom, 0.05)
  const labels: ReactNode[] = []

  for (const element of elements) {
    if (element.kind === 'rope') {
      const points = getRopeControlPoints(element, gears)
      if (points.length >= 2) {
        const total = segmentsLength(points)
        if (total > 1) {
          const mid = pointAtFraction(points, 0.5)
          labels.push(
            <MeasurementLabel
              key={`len-${element.id}`}
              x={mid.x}
              y={mid.y - fontSize}
              text={formatLength(total, calibration)}
              color={LENGTH_COLOR}
              fontSize={fontSize}
              strokeWidth={strokeWidth}
            />,
          )
        }
        // Deviation angle at each interior vertex (how far the rope is pushed
        // off a straight line: 180deg internal = 0deg deviation).
        for (let i = 1; i < points.length - 1; i++) {
          // Skip degenerate vertices (coincident points, e.g. a deleted gear
          // collapsing a contact to the origin) so they don't render a false 180° angle.
          const legIn = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y)
          const legOut = Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y)
          if (legIn < 1 || legOut < 1) continue
          const deviation = 180 - interiorAngleDeg(points[i - 1], points[i], points[i + 1])
          if (deviation < 1) continue
          labels.push(
            <MeasurementLabel
              key={`ang-${element.id}-${i}`}
              x={points[i].x}
              y={points[i].y - fontSize * 1.6}
              text={formatAngle(deviation)}
              color={SEVERITY_COLOR[deviationSeverity(deviation)]}
              fontSize={fontSize}
              strokeWidth={strokeWidth}
            />,
          )
        }
      }
      // Fall clearance for free ends that drop to the ground.
      for (const end of [element.start, element.end]) {
        if (end.mode === 'free' && end.fallsToGround && end.y < groundY) {
          const drop = groundY - end.y
          labels.push(
            <Group key={`fall-${element.id}-${end.x}-${end.y}`} listening={false}>
              <Line points={[end.x, end.y, end.x, groundY]} stroke={CLEARANCE_COLOR} strokeWidth={strokeWidth} dash={[6 / Math.max(zoom, 0.05), 4 / Math.max(zoom, 0.05)]} />
              <MeasurementLabel
                x={end.x}
                y={(end.y + groundY) / 2}
                text={`drop ${formatLength(drop, calibration)}`}
                color={CLEARANCE_COLOR}
                fontSize={fontSize}
                strokeWidth={strokeWidth}
              />
            </Group>,
          )
        }
      }
    } else if (element.kind === 'shape' && (element.shapeType === 'line' || element.shapeType === 'arrow')) {
      const length = Math.hypot(element.width, element.height)
      if (length > 1) {
        labels.push(
          <MeasurementLabel
            key={`len-${element.id}`}
            x={element.x + element.width / 2}
            y={element.y + element.height / 2 - fontSize}
            text={formatLength(length, calibration)}
            color={LENGTH_COLOR}
            fontSize={fontSize}
            strokeWidth={strokeWidth}
          />,
        )
      }
    }
  }

  return <Group listening={false}>{labels}</Group>
}

type WarningLayerProps = {
  elements: DrawingElement[]
  gears: GearElement[]
  zoom: number
}

/**
 * Read-only overlay flagging redirects (deviations / pulleys) where the
 * resultant load on the anchor reaches or exceeds the line tension. Pure
 * geometry — only caution/danger redirects are drawn so a clean rig stays
 * uncluttered.
 */
export function WarningLayer({ elements, gears, zoom }: WarningLayerProps) {
  const fontSize = 12 / Math.max(zoom, 0.05)
  const strokeWidth = 1 / Math.max(zoom, 0.05)
  const badges: ReactNode[] = []

  for (const element of elements) {
    if (element.kind !== 'rope') continue
    const points = getRopeControlPoints(element, gears)
    if (points.length < 3) continue
    for (let i = 1; i < points.length - 1; i++) {
      // Skip degenerate vertices (coincident points, e.g. a deleted gear
      // collapsing a contact to the origin) so they don't render a false 180° redirect.
      const legIn = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y)
      const legOut = Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y)
      if (legIn < 1 || legOut < 1) continue
      const deviation = 180 - interiorAngleDeg(points[i - 1], points[i], points[i + 1])
      const severity = deviationSeverity(deviation)
      if (severity === 'ok') continue
      const loadFactor = redirectLoadFactor(deviation)
      const color = SEVERITY_COLOR[severity]
      badges.push(
        <Group key={`warn-${element.id}-${i}`} x={points[i].x} y={points[i].y} listening={false}>
          <Circle radius={9 / Math.max(zoom, 0.05)} stroke={color} strokeWidth={strokeWidth * 2} />
          <MeasurementLabel
            x={0}
            y={fontSize * 1.7}
            text={`\u26a0 ${formatLoadFactor(loadFactor)}`}
            color={color}
            fontSize={fontSize}
            strokeWidth={strokeWidth}
          />
        </Group>,
      )
    }
  }

  return <Group listening={false}>{badges}</Group>
}
